import React, { useState } from 'react';
import SEO from '../components/SEO';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: string;
  items: FAQItem[];
}

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggleFAQ = (catIndex: number, itemIndex: number) => {
    const key = `${catIndex}-${itemIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  const faqCategories: FAQCategory[] = [
    {
      title: 'General Questions',
      icon: 'ℹ️',
      items: [
        {
          question: 'What does the name AlmeOne mean?',
          answer: 'AlmeOne is rooted in meaning and ambition. It blends the Arabic word "علمة" (ʿAlmah), meaning knowledge and wisdom, with the suffix "-One" to reflect singularity, leadership, and a unified vision of intelligence and innovation.'
        },
        {
          question: 'Where is AlmeOne based and what areas do you serve?',
          answer: 'AlmeOne is headquartered in Doha, Qatar. Through our partnership with Sradha Technologies, we serve clients locally in the Middle East, as well as globally across the United States, Canada, India, and other international technology hubs.'
        },
        {
          question: 'Who are your technology partners?',
          answer: 'Our core technology R&D partner is Sradha Technologies. This partnership allows us to deploy cutting-edge AI research, machine learning models, and complex cloud-native architectures locally with exceptional execution support.'
        }
      ]
    },
    {
      title: 'AI Consultation & Systems',
      icon: '🤖',
      items: [
        {
          question: 'What kind of AI services do you offer?',
          answer: 'We provide end-to-end AI consulting, custom Machine Learning model development, Computer Vision solutions (such as automated damage inspection and security monitoring), Natural Language Processing (NLP) for intelligent document parsing, and predictive analytics models.'
        },
        {
          question: 'Can your AI systems integrate with our legacy software?',
          answer: 'Yes! We specialize in "AI Integration Services." Our team designs custom API pipelines and middleware that securely connect state-of-the-art AI models directly into your existing ERP, CRM, or legacy database systems without disrupting daily operations.'
        },
        {
          question: 'How do you ensure data privacy when building AI models?',
          answer: 'Data security is our primary focus. We sign strict non-disclosure agreements, offer containerized local deployments (on-premise edge computing), and follow international security protocols (like ISO 27001) to ensure your proprietary company data is never shared or exposed.'
        }
      ]
    },
    {
      title: 'Warehouse Automation',
      icon: '🏭',
      items: [
        {
          question: 'How does the automated container damage inspection system work?',
          answer: 'Our system uses high-resolution cameras installed at warehouse/terminal gates. As a truck passes at up to 15 km/h, the cameras capture structural images. Our Vision AI processes these frames in under 3 seconds, automatically detecting, localizing, and logging dents, structural damage, rust, and door-latch issues.'
        },
        {
          question: 'Do you provide the hardware needed for warehouse automation?',
          answer: 'We supply the complete hardware-software package. This includes 3D depth sensors, industrial camera rigs, edge servers, weighing scales, and custom mounting frameworks. We also handle layout configuration and installation.'
        },
        {
          question: 'What is automated cargo dimensioning?',
          answer: 'It is a system that uses overhead 3D depth sensors and integrated weighing scales to capture a package\'s length, width, height, and weight in less than 0.5 seconds as soon as it is placed on the scale, replacing manual tape measures completely.'
        }
      ]
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Frequently Asked Questions - AlmeOne"
        description="Find answers to common questions about AlmeOne's AI consulting, computer vision solutions, warehouse automation, and logistics technology."
        keywords="FAQ, AlmeOne help, warehouse automation questions, AI integration FAQ, Qatar tech support"
      />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-gold/20 py-20 text-white relative">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Frequently Asked <span className="text-gold">Questions</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Got questions? We have answers. Learn more about our technology systems, services, and integration capabilities.
          </p>
        </div>
      </section>

      {/* Accordion List */}
      <section className="max-w-4xl mx-auto px-5 py-16">
        <div className="space-y-10">
          {faqCategories.map((category, catIndex) => (
            <div key={catIndex} className="bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-black mb-6 flex items-center gap-3 pb-3 border-b border-gray-100">
                <span className="text-2xl sm:text-3xl">{category.icon}</span>
                {category.title}
              </h2>
              
              <div className="space-y-4">
                {category.items.map((item, itemIndex) => {
                  const key = `${catIndex}-${itemIndex}`;
                  const isOpen = openIndex === key;
                  
                  return (
                    <div 
                      key={itemIndex}
                      className={`border rounded-xl transition-all duration-300 ${
                        isOpen ? 'border-gold bg-gold/5' : 'border-gray-100 bg-white hover:border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => toggleFAQ(catIndex, itemIndex)}
                        className="w-full text-left p-5 flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-black focus:outline-none"
                      >
                        <span>{item.question}</span>
                        <span className={`text-gold text-lg transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>
                      
                      <div 
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          isOpen ? 'max-h-[500px] border-t border-gray-100' : 'max-h-0'
                        }`}
                      >
                        <p className="p-5 text-gray-600 text-sm sm:text-base leading-relaxed bg-white/50">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions banner */}
        <div className="mt-16 bg-gradient-to-br from-black via-gray-900 to-black text-white p-8 sm:p-10 rounded-2xl text-center border border-gold/25 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent"></div>
          <div className="relative z-10">
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Still Have Questions?</h3>
            <p className="text-gray-300 mb-6 text-sm sm:text-base max-w-lg mx-auto">
              If you did not find the answer to your question, feel free to drop us a message or schedule a consultation.
            </p>
            <a 
              href="/contact"
              className="inline-block bg-gold text-black font-bold px-6 py-3 rounded-xl hover:bg-gold-dark transition-all transform hover:-translate-y-0.5 text-sm sm:text-base"
            >
              Contact Us Directly
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
